import moment from 'moment';

export const getUpdatedEducationData = (educationList = []) => {
  return educationList.map(
    ({ degreeStreamMapId, educationLevel, tcUrl, ...rest }) => {
      if (educationLevel === 'tenth' || educationLevel === 'twelfth') {
        return { ...rest, educationLevel, tcUrl: tcUrl || {} }
      } else {
        return { ...rest, degreeStreamMapId, educationLevel, tcUrl }
      }
    }
  )
}

export const formatStudentData = (student) => {
  if (!student) return null;
  
  const personalData = student?.studentPersonalProfile;

  return {
    admin: {
      email: student?.admin?.email,
      phoneNumber: student?.admin?.phone_number,
      firstName: student?.firstName,
      lastName: student?.lastName,
      dob: personalData?.dob,
      gender: personalData?.gender,
      countryCode: student?.admin?.country_code,
    },
    student: {
      uniRollNo: student?.uniRollNo,
      startBatch: student?.startBatch,
      endBatch: student?.endBatch,
      middelName: student?.middelName,
      passedOut: student?.passedOut,
      profileUrl: student?.profileUrl,
      isExpCandidate: student?.isExpCandidate,
      instituteCampusId: student?.instituteCampusId,
      instituteCampusName: student?.instituteCampusName,
    },
    studentPersonalProfile: {
      father: personalData?.father,
      mother: personalData?.mother,
      caste: personalData?.caste,
      religion: personalData?.religion,
      registraionNo: student?.uniRollNo,
      idProof: personalData?.idProof || [],
      addressProof: personalData?.addressProof || [],
      secondaryEmail: personalData?.secondaryEmail,
      preferredJobLocation: personalData?.preferredJobLocation || [],
      languages: personalData?.languages || [],
      hobbies: personalData?.hobbies,
      socialMedia: personalData?.socialMedia || [],
      permAddrLine1: personalData?.permAddrLine1,
      permAddrLine2: personalData?.permAddrLine2,
      permCity: personalData?.permCity,
      permState: personalData?.permState,
      permCountry: personalData?.permCountry,
      permCityId: personalData?.permCityId,
      permStateId: personalData?.permStateId,
      permCountryId: personalData?.permCountryId,
      permPostCode: personalData?.permPostCode,
      isSameCorresponding: personalData?.isSameCorresponding,
      corrAddrLine1: personalData?.corrAddrLine1,
      corrAddrLine2: personalData?.corrAddrLine2,
      corrCity: personalData?.corrCity,
      corrState: personalData?.corrState,
      corrCountry: personalData?.corrCountry,
      corrCityId: personalData?.corrCityId,
      corrStateId: personalData?.corrStateId,
      corrCountryId: personalData?.corrCountryId,
      corrPostCode: personalData?.corrPostCode,
      isPhysicallyChallenged: personalData?.isPhysicallyChallenged,
      physicalChallenge: personalData?.physicalChallenge,
      isAnywhere: personalData?.isAnywhere,
      extraCurriculars: personalData?.extraCurriculars,
    },
    currentCourse: student?.currentCourse,
    education: getUpdatedEducationData(student?.educationProfile),
    resume: {
      objective: student?.resume?.objective,
      projects: student?.resume?.projects || [],
      internships: student?.resume?.internships || [],
      courses: student?.resume?.courses || [],
      training: student?.resume?.training || [],
      awards: student?.resume?.awards || [],
      workExperience: student?.resume?.workExperience || [],
    },
  };
};

export const formatResumeData = (data) => {
  // Create a Map to store unique skills with case normalization
  const skillsMap = new Map(); // Use Map to store normalized skills
  
  // Function to normalize and add skill
  const addSkill = (skill) => {
      const skillName = typeof skill === 'object' ? skill.name : skill;
      if (!skillName) return;
      
      const normalizedName = skillName.toLowerCase();
      // Keep the version with the most uppercase letters if different cases exist
      if (!skillsMap.has(normalizedName) || 
          (skillName.match(/[A-Z]/g) || []).length > (skillsMap.get(normalizedName).match(/[A-Z]/g) || []).length) {
          skillsMap.set(normalizedName, skillName);
      }
  };
  
  // Add skills from education and current course
  data?.education?.forEach(edu => {
      edu?.skills?.forEach(skill => addSkill(skill));
  });
  
  // Add skills from current course if exists
  data?.currentCourse?.skills?.forEach(skill => addSkill(skill));

  // Add skills from work experience
  data?.resume?.workExperience?.forEach(exp => {
      exp?.skill_set?.forEach(skill => addSkill(skill));
  });

  // Add skills from internships
  data?.resume?.internships?.forEach(internship => {
      internship?.skill_set?.forEach(skill => addSkill(skill));
  });

  // Add skills from projects
  data?.resume?.projects?.forEach(project => {
      project?.skill_set?.forEach(skill => addSkill(skill));
  });

    // Add skills from courses
    data?.resume?.courses?.forEach(course => {
      course?.skill_set?.forEach(skill => addSkill(skill));
  });

  data?.resume?.courses?.forEach(course => {
    course?.skill_set?.forEach(skill => addSkill(skill));
});
  // Debug the social media data structure
  const socialMedia = data?.studentPersonalProfile?.socialMedia || [];
  const socialLinks = {
    linkedin: socialMedia.find(item => item?.link?.toLowerCase().includes('linkedin'))?.link || '',
    github: socialMedia.find(item => item?.link?.toLowerCase().includes('github'))?.link || '',
    behance: socialMedia.find(item => item?.link?.toLowerCase().includes('behance'))?.link || ''
  };

  // Education level order mapping
  const educationLevelOrder = {
    'phd': 1,
    'pg': 2,
    'p_g_diploma': 3,
    'puc': 4,
    'pd': 5,
    'ug': 6,
    'diploma': 7,
    'twelfth': 8,
    'tenth': 9
  };

  const formatDate = (date) => {
    return date ? moment(date).format('MMM YYYY') : '';
  };

  // Function to clean empty HTML tags and unnecessary whitespace
  const cleanHTML = (html) => {
    if (!html) return '';
    return html.replace(/<p[^>]*>\s*(?:<br\s*\/?>\s*)*<\/p>/g, '') // Remove <p> with only <br> and any attributes
              .replace(/<p[^>]*>\s*&nbsp;\s*<\/p>/g, '') // Remove <p> with only &nbsp;
              .replace(/<p[^>]*>\s*<\/p>/g, '') // Remove empty <p> with any attributes
              .replace(/<br\s*\/?>\s*/g, '') // Remove standalone <br>
              .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
              .replace(/\n+/g, '') // Remove newlines
              .replace(/\s+/g, ' ') // Replace multiple spaces with single space
              .trim() || html;
  };

  // Get unique skills array from the Map
  const uniqueSkills = Array.from(skillsMap.values()).sort();

  return {
    student: {
      fullName: `${data?.admin?.firstName || ''} ${data?.admin?.lastName || ''}`.trim(),
      countryCode: data?.admin?.countryCode || '',
      mobile: data?.admin?.phoneNumber || '',
      email: data?.admin?.email || '',
      city: data?.studentPersonalProfile?.permCity || '',
      state: data?.studentPersonalProfile?.permState || '',
      linkedinUrl: socialLinks.linkedin,
      githubUrl: socialLinks.github,
      behanceUrl: socialLinks.behance,
      objective: cleanHTML(data?.resume?.objective),
      extraCurriculars: cleanHTML(data?.studentPersonalProfile?.extraCurriculars || '')
    },
    education: (() => {
      const allEducation = [...(data?.education || [])];
      
      // Add current course if exists and user is not experienced
      if (data?.currentCourse && !data?.student?.isExpCandidate) {
        allEducation.unshift({
          ...data.currentCourse,
          isCurrentCourse: true,
          institutionName: data?.student?.instituteCampusName?.trim() || ''
        });
      }

      // Sort by education level first, then by endedOn
      return allEducation
        .sort((a, b) => {
          // First sort by education level
          const levelDiff = (educationLevelOrder[a.educationLevel] || 99) - (educationLevelOrder[b.educationLevel] || 99);
          if (levelDiff !== 0) return levelDiff;
          
          // If same level, sort by endedOn date
          const endedA = parseInt(a?.endedOn) || 0;
          const endedB = parseInt(b?.endedOn) || 0;
          return endedB - endedA;
        })
        .map(edu => ({
          educationLevel: edu?.educationLevel || '',
          degree: edu?.educationLevel === 'tenth' 
            ? `SSLC - ${edu?.board}`
            : edu?.educationLevel === 'twelfth'
            ? `HSC - ${edu?.board}`
            : `${edu?.degree}${edu?.department ? ` - ${edu?.department}` : ''}${edu?.specialisation ? ` (${edu?.specialisation})` : ''}`,
          board: edu?.board || '',
          institutionName: edu?.institutionName?.trim() || '',
          city: edu?.city || '',
           startDate: edu?.startedOn > 0
              ? moment(parseInt(edu?.startedOn)).format(edu?.isCurrentCourse ? 'MMM YYYY' : 'YYYY')
              : '',
            endDate: edu?.endedOn > 0
              ? moment(parseInt(edu?.endedOn)).format(edu?.isCurrentCourse ? 'MMM YYYY' : 'YYYY')
              : '',
        }));
    })(),
    workExperience: (data?.resume?.workExperience || [])
      .sort((a, b) => {
        // Sort by isCurrentlyWorking first
        if (a.isCurrentlyWorking && !b.isCurrentlyWorking) return -1;
        if (!a.isCurrentlyWorking && b.isCurrentlyWorking) return 1;
        
        // Then sort by ended_in date
        const endA = a.ended_in ? moment(a.ended_in, 'MM/YYYY').valueOf() : Date.now();
        const endB = b.ended_in ? moment(b.ended_in, 'MM/YYYY').valueOf() : Date.now();
        return endB - endA;
      })
      .map(work => ({
        roleName: work?.title || '',
        companyName: work?.organization || '',
        startDate: work?.started_in ? moment(work?.started_in, 'MM/YYYY').format('MMM YYYY') : '',
        endDate: work?.ended_in ? moment(work?.ended_in, 'MM/YYYY').format('MMM YYYY') : '',
        description: cleanHTML(work?.description || ''),
        isCurrentlyWorking: work?.isCurrentlyWorking || false,
      })),
    internships: (data?.resume?.internships || [])
      .sort((a, b) => {
        const endA = a.ended_in ? moment(a.ended_in, 'MM/YYYY').valueOf() : 0;
        const endB = b.ended_in ? moment(b.ended_in, 'MM/YYYY').valueOf() : 0;
        return endB - endA;
      })
      .map(internship => ({
        roleName: internship?.role || '',
        companyName: internship?.organization || '',
        startDate: internship?.started_in ? moment(internship?.started_in, 'MM/YYYY').format('MMM YYYY') : '',
        endDate: internship?.ended_in ? moment(internship?.ended_in, 'MM/YYYY').format('MMM YYYY') : '',
        description: cleanHTML(internship?.description || ''),
        isCurrentlyWorking: internship?.isCurrentlyWorking || false,
      })),
    projects: (data?.resume?.projects || [])
      .sort((a, b) => {
        const endA = a.ended_in ? moment(a.ended_in, 'MM/YYYY').valueOf() : 0;
        const endB = b.ended_in ? moment(b.ended_in, 'MM/YYYY').valueOf() : 0;
        return endB - endA;
      })
      .map(project => ({
        title: project?.title || '',
        description: cleanHTML(project?.description || ''),
        startDate: project?.started_in ? moment(project?.started_in, 'MM/YYYY').format('MMM YYYY') : '',
        endDate: project?.ended_in ? moment(project?.ended_in, 'MM/YYYY').format('MMM YYYY') : '',
      })),
    skills: uniqueSkills,
    certifications: (data?.resume?.courses || [])
      .sort((a, b) => {
        const yearA = parseInt(a.endYear) || 0;
        const yearB = parseInt(b.endYear) || 0;
        return yearB - yearA;
      })
      .map(course => ({
        courseName: course?.title || '',
        instituteName: course?.organization || '',
        endYear: moment(course?.ended_in, 'MM/YYYY').format('YYYY') || '',
      })),
  };
};
